import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, orderBy, 
  where, Timestamp, doc, updateDoc, deleteDoc 
} from 'firebase/firestore';
import Select from 'react-select';
import Swal from 'sweetalert2';
import moment from 'moment';

function Dipentry() {
  const [orderdate, setOrderdate] = useState(moment().format("YYYY-MM-DD"));
  const [tanks, setTanks] = useState([]);
  const [selectedTank, setSelectedTank] = useState(null);
  
  // Entry States
  const [dipReading, setDipReading] = useState("");
  const [physicalLiters, setPhysicalLiters] = useState("");
  const [bookStock, setBookStock] = useState(0);

  const [dipHistory, setDipHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
  });

  useEffect(() => {
    if (!userId) return;

    // Fetch Tanks
    const unsubTanks = onSnapshot(
      query(collection(db, "tanks"), where("userId", "==", userId)), 
      (snap) => {
        setTanks(snap.docs.map(d => ({ 
          id: d.id, 
          ...d.data(), 
          label: `${d.data().tankName} (${d.data().fuelType})`, 
          value: d.id 
        })));
      }
    );

    // Fetch History
    const q = query(
      collection(db, "dip_entries"), 
      where("userId", "==", userId), 
      where("date", "==", orderdate), 
      orderBy("created", "desc")
    );
    const unsubHistory = onSnapshot(q, (snap) => {
      setDipHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubTanks(); unsubHistory(); };
  }, [orderdate, userId]);

  const handleTankSelect = (opt) => {
    setSelectedTank(opt);
    setBookStock(opt.currentStock || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTank || !dipReading || !physicalLiters) {
      return Toast.fire({ icon: 'warning', title: 'Please fill all fields' });
    }

    const variance = parseFloat(physicalLiters) - parseFloat(bookStock);
    setLoading(true);

    const payload = {
      userId,
      tankId: selectedTank.id,
      tankName: selectedTank.tankName,
      fuelType: selectedTank.fuelType,
      dipMm: parseFloat(dipReading),
      physicalQty: parseFloat(physicalLiters),
      bookQty: parseFloat(bookStock),
      variance: variance,
      date: orderdate,
      updatedAt: Timestamp.now()
    };

    try {
      if (editId) {
        await updateDoc(doc(db, "dip_entries", editId), payload);
        Toast.fire({ icon: 'success', title: 'Entry Updated' });
      } else {
        await addDoc(collection(db, "dip_entries"), { ...payload, created: Timestamp.now() });
        Toast.fire({ icon: 'success', title: 'Dip Recorded' });
      }
      resetForm();
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setDipReading(item.dipMm);
    setPhysicalLiters(item.physicalQty);
    setBookStock(item.bookQty);
    const tankObj = tanks.find(t => t.id === item.tankId);
    setSelectedTank(tankObj || null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this entry?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete'
    });
    if (result.isConfirmed) {
      await deleteDoc(doc(db, "dip_entries", id));
      Toast.fire({ icon: 'success', title: 'Deleted' });
    }
  };

  const resetForm = () => {
    setEditId(null);
    setDipReading("");
    setPhysicalLiters("");
    setBookStock(0);
    setSelectedTank(null);
  };

  return (
    <div className="content-wrapper bg-light min-vh-100">
      <section className="content-header py-3 px-4">
        <div className="container-fluid bg-white p-3 shadow-sm rounded-lg border">
          <div className="row align-items-center">
            <div className="col-sm-6">
              <h4 className="m-0 font-weight-bold text-navy">Tank Dip Entry</h4>
            </div>
            <div className="col-sm-6 text-right">
              <input type="date" className="form-control d-inline-block border-primary" style={{width: 180}} 
                value={orderdate} onChange={e => setOrderdate(e.target.value)} />
            </div>
          </div>
        </div>
      </section>

      <section className="content px-4">
        <div className="container-fluid">
          {/* ENTRY FORM */}
          <div className={`card ${editId ? 'card-warning' : 'card-primary'} card-outline shadow-sm`}>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="row align-items-end">
                <div className="col-md-3">
                  <label className="small font-weight-bold">Select Tank</label>
                  <Select options={tanks} value={selectedTank} onChange={handleTankSelect} />
                </div>
                <div className="col-md-2">
                  <label className="small font-weight-bold">Dip (MM)</label>
                  <input type="number" className="form-control" value={dipReading} onChange={e => setDipReading(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="small font-weight-bold">Actual Liters</label>
                  <input type="number" className="form-control" value={physicalLiters} onChange={e => setPhysicalLiters(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="small font-weight-bold text-muted">Book Stock</label>
                  <input type="number" className="form-control bg-light" value={bookStock} readOnly />
                </div>
                <div className="col-md-3">
                  <button type="submit" disabled={loading} className={`btn btn-block font-weight-bold ${editId ? 'btn-warning text-white' : 'btn-primary'}`}>
                    {editId ? 'UPDATE RECORD' : 'SAVE DIP'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* HISTORY TABLE */}
          <div className="card shadow-sm mt-4 border-0">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle m-0">
                  <thead className="bg-navy text-white">
                    <tr>
                      <th style={{ width: '60px' }} className="text-center">Sr.</th>
                      <th>Tank Name</th>
                      <th>Reading</th>
                      <th>Physical Stock</th>
                      <th>Variance</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dipHistory.length > 0 ? dipHistory.map((item, index) => (
                      <tr key={item.id}>
                        <td className="text-center font-weight-bold text-muted">{index + 1}</td>
                        <td>
                          <span className="font-weight-bold">{item.tankName}</span>
                          <div className="small text-muted">{item.fuelType}</div>
                        </td>
                        <td>{item.dipMm} mm</td>
                        <td>{item.physicalQty} L</td>
                        <td className={item.variance < 0 ? 'text-danger font-weight-bold' : 'text-success font-weight-bold'}>
                          {item.variance > 0 ? `+${item.variance.toFixed(2)}` : item.variance.toFixed(2)} L
                        </td>
                        <td className="text-center">
                          <button className="btn btn-sm btn-link text-primary" onClick={() => handleEdit(item)}><i className="fas fa-edit"></i></button>
                          <button className="btn btn-sm btn-link text-danger" onClick={() => handleDelete(item.id)}><i className="fas fa-trash"></i></button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="6" className="text-center py-4 text-muted">No records found for this date.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Dipentry;